import React, { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import { useNavigate } from '@lib/react-router-dom'

import { useResolvedAppTestProduct } from '@pages/ProductRegistration/AppropriatenessTest/hooks/useResolvedAppTestProduct'

import { useAccountReadContext, useAccountWriteContext } from '@context/AccountContextContext'
import { useSessionLanguage } from '@context/SessionSettingsContext'

import { hasConsentedToLegalDocuments } from '@utils/AccountAccess/accountDocumentStatuses'

import { LegalDocumentDto } from '@model/CompanyLegalDocumentDto'
import { TickmillProductType } from '@model/TickmillProductType'

import { Loading } from '@shared/components/Loading/Loading'

import { LegalDocumentsForm, LegalDocumentsValues } from '../LegalDocuments/LegalDocumentsForm'
import { useProductLegalDocumentsStepPage } from '../LegalDocuments/ProductLegalDocumentsPage'

export const ProductLegalDocumentsStepPage: React.FC = () => {
  const { t } = useTranslation()

  const navigate = useNavigate()
  const locale = useSessionLanguage()
  const { refreshAccount } = useAccountWriteContext()
  const resolvedProductId = useResolvedAppTestProduct()
  const appTestPath = `/dashboard/product-registration/${resolvedProductId}/appropriateness-test`
  const isFuturesProduct = resolvedProductId === TickmillProductType.ETD

  const { documents, mutation, marketDataClassificationId } = useProductLegalDocumentsStepPage({
    productId: resolvedProductId,
  })
  const { account } = useAccountReadContext()

  useEffect(() => {
    if (documents.isLoading) return

    if (documents.data.length <= 0 || hasConsentedToLegalDocuments(account, resolvedProductId)) {
      navigate(appTestPath, { replace: true })
    }
  }, [
    account,
    appTestPath,
    documents.data.length,
    documents.isLoading,
    navigate,
    resolvedProductId,
  ])

  const handleSubmitDocuments = async (values: LegalDocumentsValues) => {
    await mutation.mutate(values, async () => {
      await refreshAccount(locale)
      navigate(appTestPath, { replace: true })
    })
  }

  return (
    <Loading isLoading={documents.isLoading} showLoadingIcon>
      <LegalDocumentsForm
        title={isFuturesProduct ? t('Sign up.Registration Futures Trading') : t('Sign up.Legal')}
        documents={documents.data}
        groupReferences={groupReferences(documents.data)}
        marketDataClassificationId={marketDataClassificationId}
        onSubmit={handleSubmitDocuments}
      />
    </Loading>
  )
}

const groupReferences = (documents: LegalDocumentDto[] = []): (string | null)[] => {
  const groupReferences = documents.map((x) => x.groupReference)

  return [...new Set(groupReferences)]
}
